import React, { useMemo } from 'react';
import { translate } from '../../Services/translation';
import ProductCard from './ui/ProductCard';
import { Order } from '../../Types/Order';
import { EditorStringModel } from '../../Types/EditorString';
import SalesAddress from '../../Types/OrderDetailAddress';
import clsx from 'clsx';

interface OrderDetailProps {
  order: Order;
  isConfirmation: boolean;
  confirmationTitle?: string;
  confirmationText?: EditorStringModel;
  confirmationExtraText?: EditorStringModel;
}

const OrderDetailAddress = ({ address }: { address: SalesAddress }) => {

  const countriesInChannelList = globalThis.LitiumConstants.countriesInChannel;
  const country = countriesInChannelList.find(x => x.value == address.country)

  return (
    <>
      {address.organizationName && <span>{address.organizationName}</span>}
      <span>
        {address.customData.shipToName && address.customData.shipToName}
      </span>
      <span>§
        {address.customData.shipToName2 && address.customData.shipToName2}
      </span>
      <span>{address.address1}</span>
      {address.address2 && <span>{address.address2}</span>}
      <span>{address.zipCode} {address.city}</span>
      <span>{country.text}</span>
    </>
  );
};

const OrderDetail = ({
  order,
  isConfirmation,
  confirmationTitle,
  confirmationText,
  confirmationExtraText,
}: OrderDetailProps) => {
  const deliveryAddress = useMemo(
    () => (order.deliveries?.length ? order.deliveries[0].address : null),
    [order.deliveries]
  );

  return (
    <div
      className={clsx(
        'container flex flex-col gap-4 bg-gray-100 pb-6 pt-4',
        isConfirmation
          ? ' lg:items-center lg:pb-12 lg:pt-12'
          : 'mx-[-0.8rem] my-0 mb-6 md:mx-[-1.2rem] md:my-0 md:mb-6 lg:m-[unset] lg:items-center'
      )}
    >
      {isConfirmation ? (
        <div className="rounded bg-white p-4 lg:w-full lg:max-w-[660px] lg:p-6">
          <h1 className="mb-2">{confirmationTitle}</h1>
          <div className="flex flex-col gap-2 text-xl lg:flex-row lg:gap-6">
            <span>
              {translate('general.ordernumber')}:
              <span className="font-bold"> {order.externalOrderID}</span>
            </span>
            <span>
              {translate('orderdetail.orderdate')}:
              <span className="font-bold"> {order.orderDate}</span>
            </span>
          </div>
          {confirmationText?.value && (
            <div
              className="mt-2 text-xl"
              dangerouslySetInnerHTML={{
                __html: confirmationText?.value,
              }}
            ></div>
          )}
        </div>
      ) : (
        <div className="rounded bg-white p-4 lg:w-full lg:max-w-[660px] lg:p-6">
          <h1 className="mb-2">
            {translate('general.ordernumber')}: {order.externalOrderID}
          </h1>
          <div className="flex flex-col gap-2 text-xl lg:flex-row lg:gap-6">
            <span>
              {translate('orderdetail.orderdate')}:
              <span className="font-bold"> {order.orderDate}</span>
            </span>
            <span>
              {translate('orderdetail.orderstatus')}:
              <span className="font-bold"> {order.status}</span>
            </span>
          </div>
        </div>
      )}
      <div className="flex flex-col rounded bg-white p-0 lg:w-full lg:max-w-[660px] ">
        {order.orderRows.map((product) => (
          <ProductCard product={product} key={product.deliveryId} />
        ))}
        <div className="p-4 text-xl lg:p-6 [&_div:not(:last-child)]:mb-2">
          <div className="flex justify-between">
            <span>{translate('checkout.order.sum')}</span>
            <span>{order.orderTotal}</span>
          </div>
          {order.orderTotalDiscountAmount && (
            <div className="flex justify-between text-error-500">
              <span>{translate('checkout.order.saved')}</span>
              <span>{order.orderTotalDiscountAmount}</span>
            </div>
          )}
          {!order.isBusinessCustomer && (
            <div className="flex justify-between">
              <span>{translate('checkout.order.shipping')}</span>
              <span>{order.orderTotalDeliveryCost}</span>
            </div>
          )}
          {order.orderTotalFee && (
            <div className="flex justify-between">
              <span>{translate('checkout.order.fee')}</span>
              <span>{order.orderTotalFee}</span>
            </div>
          )}
          <div className="flex justify-between font-bold">
            <span>{translate('orderdetail.grandtotal')}</span>
            <span>{order.orderGrandTotal}</span>
          </div>
          <div className="flex justify-between">
            <span>{translate('checkout.order.vat')}</span>
            <span>{order.orderTotalVat}</span>
          </div>
        </div>
      </div>
      {isConfirmation && confirmationExtraText?.value && (
        <div
          className="rounded bg-white p-4 text-xl lg:w-full lg:max-w-[660px] lg:p-6"
          dangerouslySetInnerHTML={{
            __html: confirmationExtraText?.value,
          }}
        ></div>
      )}
      <div className="grid flex-wrap gap-6 rounded bg-white p-4 lg:w-full lg:max-w-[660px] lg:grid-cols-2 lg:p-6">
        {deliveryAddress && (
          <div className="flex flex-col text-xl capitalize">
            <span className="mb-2 font-bold">
              {translate('orderdetail.deliveryaddress')}
            </span>
            <OrderDetailAddress address={deliveryAddress} />
          </div>
        )}
        {order.billingAddress && (
          <div className="flex flex-col text-xl capitalize">
            <span className="mb-2 font-bold">
              {translate('orderdetail.billingaddress')}
            </span>
            <OrderDetailAddress address={order.billingAddress} />
          </div>
        )}
        <div className="flex flex-col text-xl capitalize">
          <span className="mb-2 font-bold">
            {translate('orderdetail.paymentmethod')}
          </span>
          <span>{order.paymentMethod}</span>
        </div>
        <div className="flex flex-col text-xl capitalize">
          <span className="mb-2 font-bold">
            {translate('orderdetail.deliverymethod')}
          </span>
          <span>
            {order.deliveryMethod}{' '}
            {order.selectedServicePointName.length > 0 &&
              `: ${order.selectedServicePointName}`}
          </span>
        </div>
        {order.preferredDeliveryDate &&
          order.preferredDeliveryDate.length > 0 && (
            <div className="flex flex-col text-xl capitalize">
              <span className="mb-2 font-bold">
                {translate('orderdetail.preferreddeliverydate')}
              </span>
              <span>{order.preferredDeliveryDate}</span>
            </div>
          )}
      </div>
    </div>
  );
};

export default OrderDetail;
